import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import { computeStatisticsReport, type StatsType } from "../model/results/statisticsReport.ts";
import { unitSystemOf } from "../model/units.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { localeFor } from "../i18n/language.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { NumberInput } from "./fields.tsx";
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_SERIES_PRIMARY,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";

type Category = "node" | "link" | "subcatchment";
type NodeVariable = "depth" | "head";

const STATS_TYPES: StatsType[] = ["mean", "peak", "total", "duration"];

// One combined page rather than the real GUI's 4 separate tabs (Summary/
// Events/Histogram/Frequency Plot) — Summary always shown, a toggle
// switches between the Events table and the Frequency plot; Histogram
// isn't ported.
export function StatisticsReportDialog({
  project,
  results,
  flowUnits,
  onClose,
}: {
  project: SwmmProject;
  results: SimulationResults;
  flowUnits: string;
  onClose: () => void;
}) {
  const t = useT();
  const language = useLanguage();
  const sys = unitSystemOf(flowUnits);
  const lengthUnit = sys === "US" ? "ft" : "m";
  const flowUnitLabel = flowUnits.toLowerCase();

  const namesByCategory: Record<Category, string[]> = useMemo(
    () => ({
      node: project.nodes.map((n) => n.name),
      link: project.links.map((l) => l.name),
      subcatchment: project.subcatchments.map((s) => s.name),
    }),
    [project]
  );

  const [category, setCategory] = useState<Category>(
    namesByCategory.link.length > 0 ? "link" : "node"
  );
  const [elementName, setElementName] = useState<string>(() => namesByCategory[category][0] ?? "");
  const [nodeVariable, setNodeVariable] = useState<NodeVariable>("depth");
  const [minEventValue, setMinEventValue] = useState(0);
  const [minEventVolume, setMinEventVolume] = useState(0);
  const [minEventDeltaHours, setMinEventDeltaHours] = useState(6);
  const [statsType, setStatsType] = useState<StatsType>("peak");
  const [view, setView] = useState<"events" | "frequency">("events");

  const isFlowLike = category !== "node";
  const effectiveStatsType = !isFlowLike && statsType === "total" ? "peak" : statsType;

  const values =
    category === "node"
      ? (nodeVariable === "head" ? results.nodeHead : results.nodeDepth).get(elementName)
      : category === "link"
        ? results.linkFlow.get(elementName)
        : results.subcatchmentRunoff.get(elementName);

  const variableLabel =
    category === "node"
      ? t(nodeVariable === "head" ? "results.head" : "results.depth")
      : category === "link"
        ? t("results.flow")
        : t("results.runoff");
  const variableUnit = category === "node" ? lengthUnit : flowUnitLabel;
  // Duration is always in hours; Total is a flow×time integral with no
  // clean single physical unit; Mean/Peak keep the raw variable's unit.
  const valueUnit =
    effectiveStatsType === "duration"
      ? "h"
      : effectiveStatsType === "total"
        ? `${variableUnit}·h`
        : variableUnit;

  const report = useMemo(() => {
    if (!values) return null;
    return computeStatisticsReport(
      results.times,
      values,
      isFlowLike ? values : null,
      minEventValue,
      minEventVolume,
      minEventDeltaHours,
      effectiveStatsType
    );
  }, [
    values,
    results.times,
    isFlowLike,
    minEventValue,
    minEventVolume,
    minEventDeltaHours,
    effectiveStatsType,
  ]);

  return (
    <Dialog title={t("statistics.title")} onClose={onClose} wide>
      <div className="options-section">
        <label className="options-field">
          {t("statistics.category")}
          <select
            value={category}
            onChange={(e) => {
              const next = e.target.value as Category;
              setCategory(next);
              setElementName(namesByCategory[next][0] ?? "");
            }}
          >
            <option value="node">{t("category.node")}</option>
            <option value="link">{t("category.link")}</option>
            <option value="subcatchment">{t("category.subcatchment")}</option>
          </select>
        </label>
        <label className="options-field">
          {t("statistics.element")}
          <select value={elementName} onChange={(e) => setElementName(e.target.value)}>
            {namesByCategory[category].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        {category === "node" && (
          <label className="options-field">
            {t("statistics.variable")}
            <select
              value={nodeVariable}
              onChange={(e) => setNodeVariable(e.target.value as NodeVariable)}
            >
              <option value="depth">{t("results.depth")}</option>
              <option value="head">{t("results.head")}</option>
            </select>
          </label>
        )}
        <label className="options-field">
          {t("statistics.statType")}
          <select value={statsType} onChange={(e) => setStatsType(e.target.value as StatsType)}>
            {STATS_TYPES.filter((s) => isFlowLike || s !== "total").map((s) => (
              <option key={s} value={s}>
                {t(`statistics.statType.${s}` as const)}
              </option>
            ))}
          </select>
        </label>
        <label className="options-field">
          {t("statistics.minEventValue")} ({variableUnit})
          <NumberInput value={minEventValue} onChange={setMinEventValue} />
        </label>
        {isFlowLike && (
          <label className="options-field">
            {t("statistics.minEventVolume")}
            <NumberInput value={minEventVolume} onChange={setMinEventVolume} />
          </label>
        )}
        <label className="options-field">
          {t("statistics.minEventDelta")}
          <NumberInput value={minEventDeltaHours} onChange={setMinEventDeltaHours} />
        </label>
      </div>

      {!values && <p className="property-panel-hint">{t("statistics.noResults")}</p>}

      {values && report && (
        <>
          <section className="report-section">
            <h4>{t("statistics.summary")}</h4>
            <dl className="report-stat-list">
              <div className="report-stat-row">
                <dt>{t("statistics.eventCount")}</dt>
                <dd>{report.summary.eventCount}</dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.eventFrequency")}</dt>
                <dd>{formatNumber(report.summary.eventFrequency * 100, language, 1)} %</dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.min")}</dt>
                <dd>
                  {formatNumber(report.summary.min, language, 3)} {valueUnit}
                </dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.max")}</dt>
                <dd>
                  {formatNumber(report.summary.max, language, 3)} {valueUnit}
                </dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.mean")}</dt>
                <dd>
                  {formatNumber(report.summary.mean, language, 3)} {valueUnit}
                </dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.stdDev")}</dt>
                <dd>
                  {formatNumber(report.summary.stdDev, language, 3)} {valueUnit}
                </dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.skew")}</dt>
                <dd>{formatNumber(report.summary.skew, language, 3)}</dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("statistics.period")}</dt>
                <dd>
                  {report.summary.periodYearsOrMonths}{" "}
                  {t(`statistics.period.${report.summary.periodUnit}` as const)}
                </dd>
              </div>
            </dl>
          </section>

          <div className="project-data-tabs" role="tablist">
            <button
              className={view === "events" ? "button tab-active" : "button"}
              role="tab"
              aria-selected={view === "events"}
              onClick={() => setView("events")}
            >
              {t("statistics.events")}
            </button>
            <button
              className={view === "frequency" ? "button tab-active" : "button"}
              role="tab"
              aria-selected={view === "frequency"}
              onClick={() => setView("frequency")}
            >
              {t("statistics.frequencyPlot")}
            </button>
          </div>

          {view === "events" &&
            (report.events.length === 0 ? (
              <p className="property-panel-hint">{t("statistics.noEvents")}</p>
            ) : (
              <div className="results-chart-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th className="report-table-num">{t("statistics.rank")}</th>
                      <th>{t("statistics.startDate")}</th>
                      <th className="report-table-num">{t("statistics.duration")}</th>
                      <th className="report-table-num">
                        {t("statistics.value")} ({valueUnit})
                      </th>
                      <th className="report-table-num">{t("statistics.frequencyPercent")}</th>
                      <th className="report-table-num">{t("statistics.returnPeriod")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.events.map((e) => (
                      <tr key={e.rank}>
                        <td className="report-table-num">{e.rank}</td>
                        <td>
                          {e.startTime.toLocaleString(localeFor(language), {
                            month: "short", // not "numeric" — "1/6" reads as ambiguous (day/month vs month/day)
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: "UTC", // simulation timestamps are UTC-anchored, not local
                          })}
                        </td>
                        <td className="report-table-num">
                          {formatNumber(e.duration, language, 1)}
                        </td>
                        <td className="report-table-num">{formatNumber(e.value, language, 3)}</td>
                        <td className="report-table-num">
                          {formatNumber(e.frequencyPercent, language, 2)}
                        </td>
                        <td className="report-table-num">
                          {formatNumber(e.returnPeriod, language, 2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

          {view === "frequency" &&
            (report.events.length === 0 ? (
              <p className="property-panel-hint">{t("statistics.noEvents")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid {...CHART_GRID_PROPS} />
                  <XAxis
                    dataKey="value"
                    type="number"
                    name={`${variableLabel} (${valueUnit})`}
                    tick={CHART_AXIS_TICK}
                  />
                  <YAxis
                    dataKey="frequencyPercent"
                    type="number"
                    scale="log"
                    domain={["auto", "auto"]}
                    name={t("statistics.frequencyPercent")}
                    tick={CHART_AXIS_TICK}
                  />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Scatter data={report.events} fill={CHART_SERIES_PRIMARY} />
                </ScatterChart>
              </ResponsiveContainer>
            ))}
        </>
      )}

      <DialogActions primary={{ label: t("common.ok"), onClick: onClose }} />
    </Dialog>
  );
}
